export interface Category {
  id: number;
  code: string;
  name: string;
}

export interface PersonCard {
  user_id: number;
  full_name: string;
  photo_url: string | null;
  occupation: string | null;
  company: string | null;
  looking_for_tags: string[];
  telegram_link: string | null;
  /** То, чего не угадаешь по нише бизнеса. */
  fun_fact: string | null;
}

export interface PersonDetail extends PersonCard {
  interests: string[];
  looking_for: string | null;
  can_offer: string | null;
  offer: string | null;
  website: string | null;
  /** Приходят только в своей карточке — чужие доход и возраст API не отдаёт. */
  age: number | null;
  monthly_income_usd: string | null;
  employees_count: string | null;
  contacts: string | null;
}

export type RegistrationStatus =
  | 'interested'
  | 'confirmed'
  | 'awaiting_payment'
  | 'paid'
  | 'attended'
  | 'cancelled';

export interface EventCard {
  id: number;
  title: string;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  price_idr: number;
  capacity: number | null;
  poster_url: string | null;
  visibility: 'public' | 'community' | 'club';
  category: Category;
  going_count: number;
  spots_left: number | null;
  my_status: RegistrationStatus | null;
}

export interface EventDetail extends EventCard {
  description: string | null;
  programme: string | null;
  partners: string | null;
  organizer: PersonCard | null;
  going: PersonCard[];
}

export interface Registration {
  id: number;
  event_id: number;
  status: RegistrationStatus;
  created_at: string;
}

export const LOOKING_FOR_LABELS: Record<string, string> = {
  partner: 'Партнёра',
  investor: 'Инвестора',
  employee: 'Сотрудника',
  project: 'Проект для инвестиций',
  community: 'Сообщество',
};

/** Ступень воронки. count — «дошёл до этого шага или дальше»,
 *  иначе оплативший выпадал бы из «записался» и воронка расширялась книзу. */
export interface FunnelStage {
  key: RegistrationStatus;
  label: string;
  count: number;
  from_top: number;
  from_prev: number | null;
}

export interface FunnelMoney {
  collected_idr: number;
  awaiting_idr: number;
  potential_idr: number;
  lost_idr: number;
}

export interface CategoryFunnel {
  code: string;
  name: string;
  signed_up: number;
  paid: number;
  collected_idr: number;
}

export interface EventFunnel {
  event_id: number;
  title: string;
  starts_at: string;
  price_idr: number;
  capacity: number | null;
  counts: Partial<Record<RegistrationStatus, number>>;
  cancelled: number;
  collected_idr: number;
  fill_percent: number | null;
}

export interface StuckRegistration {
  registration_id: number;
  user_id: number;
  full_name: string;
  event_id: number;
  event_title: string;
  starts_at: string;
  status: RegistrationStatus;
  waiting_hours: number;
  amount_idr: number;
}

export interface PeopleStats {
  total: number;
  with_questionnaire: number;
  new_this_week: number;
  bot_blocked: number;
  paying: number;
  repeat_paying: number;
}

export interface AdminStats {
  period: StatsPeriod;
  generated_at: string;
  funnel: FunnelStage[];
  money: FunnelMoney;
  cancelled: number;
  by_category: CategoryFunnel[];
  events: EventFunnel[];
  stuck: StuckRegistration[];
  people: PeopleStats;
}

export type StatsPeriod = 'upcoming' | 'past' | 'all';

export interface Participant {
  registration_id: number;
  user_id: number;
  full_name: string;
  occupation: string | null;
  telegram_link: string | null;
  status: RegistrationStatus;
  created_at: string;
}

export const STATUS_LABELS: Record<RegistrationStatus, string> = {
  interested: 'Записался',
  confirmed: 'Подтвердил',
  awaiting_payment: 'Ждёт оплаты',
  paid: 'Оплатил',
  attended: 'Пришёл',
  cancelled: 'Отменил',
};

export interface BonusEntry {
  id: number;
  kind: string;
  label: string;
  /** Знак значим: начисление положительное, списание отрицательное. */
  amount_idr: number;
  category: string | null;
  note: string | null;
  created_at: string;
}

export interface InvitedPerson {
  user_id: number | null;
  full_name: string | null;
  joined_at: string | null;
  earned_idr: number;
}

export interface Bonuses {
  balance_idr: number;
  total_earned_idr: number;
  total_spent_idr: number;
  invite_url: string;
  invited: InvitedPerson[];
  history: BonusEntry[];
}

export interface ClubSeat {
  seat_number: number;
  user_id: number | null;
  full_name: string | null;
  niche: string | null;
  ends_on: string | null;
  status: string | null;
}

export interface Club {
  total_seats: number;
  free_seats: number;
  seats: ClubSeat[];
}

export interface EventComment {
  id: number;
  user_id: number;
  author: string;
  body: string;
  created_at: string;
  mine: boolean;
}

export interface EventReview {
  id: number;
  user_id: number;
  author: string;
  rating: number;
  body: string | null;
  created_at: string;
  mine: boolean;
}

export interface Medal {
  code: string;
  title: string;
  description: string;
  icon: string;
  kind: 'auto' | 'manual';
  awarded_at?: string | null;
  note?: string | null;
}

export interface HistoryItem {
  at: string;
  kind: 'registration' | 'payment' | 'bonus' | 'medal';
  icon: string;
  title: string;
  subtitle: string | null;
  amount_idr: number | null;
  who: string | null;
  user_id: number | null;
}

export interface SettingParam {
  key: string;
  label: string;
  hint: string;
  kind: 'text' | 'int' | 'bool' | 'money';
  group: string;
  value: string | number | boolean;
}

export interface ReferralRate {
  category_code: string;
  category_name: string;
  level: number;
  level_label: string;
  level_hint: string;
  rate_percent: number;
  effective_from: string;
}

export interface AppSettings {
  params: SettingParam[];
  rates: ReferralRate[];
  /** Сколько уходит с каждой оплаты по всей цепочке. */
  total_chain_percent: Record<string, number>;
}
